/**
 * Business settings page component for lite home navigation
 */

import { Stack } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import type { BusinessInfoSettings } from "@/types";
import SettingsHeader from "@/pages/Settings/SettingsHeader/SettingsHeader";
import BusinessInfoSetting from "@/pages/BusinessInfoSetting/BusinessInfoSetting";
import BusinessLogo from "@/components/Tax Pro Only/BusinessLogo/BusinessLogo";
import OneClickSettings from "@/pages/Settings/components/OneClickSettings";
import { updateTaxProInfo } from "@/utils/api";

interface BusinessSettingsPageProps {
  onExit?: () => void;
}

const BusinessSettingsPage: React.FC<BusinessSettingsPageProps> = () => {
  const storedBusinessInfo = localStorage.getItem("businessInfo");
  const initialBusinessInfo: BusinessInfoSettings = storedBusinessInfo
    ? JSON.parse(storedBusinessInfo)
    : ({
        businessName: "",
        businessPhone: "",
        businessEmail: "",
        businessWebsite: "",
        logoKey: "",
        consultLink: "",
        paymentLink: "",
        primaryColor: "",
        secondaryColor: "",
        loginMessage: "",
        vanitySlug: "",
      } as unknown as BusinessInfoSettings);

  const [businessInfo, setBusinessInfo] =
    useState<BusinessInfoSettings>(initialBusinessInfo);

  const formRef = useRef<HTMLFormElement | null>(null);

  // Keep local state in sync with localStorage updates
  useEffect(() => {
    const onStorage = () => {
      const bi = localStorage.getItem("businessInfo");
      if (bi) setBusinessInfo(JSON.parse(bi));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <Stack gap={24}>
      <SettingsHeader
        title="Business"
        description="View and update your business information"
      />
      <BusinessInfoSetting
        businessInfo={businessInfo}
        setBusinessInfo={setBusinessInfo}
        updateBusinessInfo={updateTaxProInfo}
        formRef={formRef}
        isSettingsPage
      >
        <BusinessLogo />
      </BusinessInfoSetting>
      <OneClickSettings />
    </Stack>
  );
};

export default BusinessSettingsPage;
